interface TimelineTask {
    id: string;
    title: string;
    subtitle: string;
    startWeek: number;
    endWeek: number;
    status: 'completed' | 'in-progress' | 'critical' | 'planning';
    teamSize?: number;
}

interface ProjectTimelineProps {
    tasks?: TimelineTask[];
    theme?: 'light' | 'dark';
}

const defaultTasks: TimelineTask[] = [
    {
        id: '1',
        title: 'Consultation',
        subtitle: 'Initial client meetings and project alignment',
        startWeek: 1,
        endWeek: 2,
        status: 'completed',
        teamSize: 3
    },
    {
        id: '2',
        title: 'Research & Scoping',
        subtitle: 'Market analysis and technical requirements',
        startWeek: 2,
        endWeek: 4,
        status: 'completed',
        teamSize: 2
    },
    {
        id: '3',
        title: 'UI/UX Implementation',
        subtitle: 'Design system and user experience creation',
        startWeek: 4,
        endWeek: 7,
        status: 'completed',
        teamSize: 2
    },
    {
        id: '4',
        title: 'Development',
        subtitle: 'Core application development and features',
        startWeek: 6,
        endWeek: 10,
        status: 'completed',
        teamSize: 3
    },
    {
        id: '5',
        title: 'Testing',
        subtitle: 'Quality assurance and performance validation',
        startWeek: 9,
        endWeek: 11,
        status: 'completed',
        teamSize: 2
    },
    {
        id: '6',
        title: 'Deployment',
        subtitle: 'Production launch and monitoring setup',
        startWeek: 11,
        endWeek: 12,
        status: 'completed',
        teamSize: 3
    }
];

const ProjectTimeline: React.FC<ProjectTimelineProps> = ({
    tasks = defaultTasks,
    theme = 'light'
}) => {
    const getTaskTheme = (taskId: string) => {
        switch (taskId) {
            case '1': // Consultation
                return {
                    cardBg: 'bg-gradient-to-br from-purple-50 to-purple-100/50',
                    border: 'border-purple-200',
                    statusBar: 'bg-purple-500',
                    statusBadge: 'bg-purple-50 text-purple-700 border border-purple-200',
                    titleColor: 'text-purple-900',
                    hover: 'hover:border-purple-300 hover:shadow-purple-100'
                };
            case '2': // Research & Scoping
                return {
                    cardBg: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
                    border: 'border-blue-200',
                    statusBar: 'bg-blue-500',
                    statusBadge: 'bg-blue-50 text-blue-700 border border-blue-200',
                    titleColor: 'text-blue-900',
                    hover: 'hover:border-blue-300 hover:shadow-blue-100'
                };
            case '3': // UI/UX Implementation
                return {
                    cardBg: 'bg-gradient-to-br from-pink-50 to-pink-100/50',
                    border: 'border-pink-200',
                    statusBar: 'bg-pink-500',
                    statusBadge: 'bg-pink-50 text-pink-700 border border-pink-200',
                    titleColor: 'text-pink-900',
                    hover: 'hover:border-pink-300 hover:shadow-pink-100'
                };
            case '4': // Development
                return {
                    cardBg: 'bg-gradient-to-br from-green-50 to-green-100/50',
                    border: 'border-green-200',
                    statusBar: 'bg-green-500',
                    statusBadge: 'bg-green-50 text-green-700 border border-green-200',
                    titleColor: 'text-green-900',
                    hover: 'hover:border-green-300 hover:shadow-green-100'
                };
            case '5': // Testing
                return {
                    cardBg: 'bg-gradient-to-br from-orange-50 to-orange-100/50',
                    border: 'border-orange-200',
                    statusBar: 'bg-orange-500',
                    statusBadge: 'bg-orange-50 text-orange-700 border border-orange-200',
                    titleColor: 'text-orange-900',
                    hover: 'hover:border-orange-300 hover:shadow-orange-100'
                };
            case '6': // Deployment
                return {
                    cardBg: 'bg-gradient-to-br from-indigo-50 to-indigo-100/50',
                    border: 'border-indigo-200',
                    statusBar: 'bg-indigo-500',
                    statusBadge: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
                    titleColor: 'text-indigo-900',
                    hover: 'hover:border-indigo-300 hover:shadow-indigo-100'
                };
            default:
                return {
                    cardBg: 'bg-white',
                    border: 'border-gray-200',
                    statusBar: 'bg-gray-500',
                    statusBadge: 'bg-gray-50 text-gray-700 border border-gray-200',
                    titleColor: 'text-gray-900',
                    hover: 'hover:border-gray-300'
                };
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Completed';
            case 'in-progress': return 'In Progress';
            case 'critical': return 'Critical';
            case 'planning': return 'Planned';
            default: return '';
        }
    };

    // Generate week headers (simplified)
    const weekHeaders = Array.from({ length: 6 }, (_, i) => ({
        start: i * 2 + 1,
        end: i * 2 + 2,
        label: `Week ${i * 2 + 1}–${i * 2 + 2}`
    }));

    return (
        <div className="w-full bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            {/* Simplified Week Headers */}
            <div className="grid grid-cols-6 gap-8 mb-12 pb-6 border-b border-gray-100 relative">
                {weekHeaders.map((week, index) => (
                    <div key={index} className="text-center relative">
                        <div className="text-sm font-semibold text-gray-900 mb-3">{week.label}</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="text-xs text-gray-500 bg-gray-50 py-2 px-3 rounded-md font-medium">W{week.start}</div>
                            <div className="text-xs text-gray-500 bg-gray-50 py-2 px-3 rounded-md font-medium">W{week.end}</div>
                        </div>
                        {/* Vertical dotted lines after each 2-week group (except the last one) */}
                        {index < weekHeaders.length - 1 && (
                            <div className="absolute top-0 -right-4 w-px h-full border-r border-dotted border-gray-200 opacity-80"></div>
                        )}
                    </div>
                ))}
            </div>

            {/* Timeline Tasks - Vertical Stack */}
            <div className="space-y-6 relative">
                {/* Background dotted lines extending through the task area */}
                {weekHeaders.slice(0, -1).map((_, index) => (
                    <div
                        key={index}
                        className="absolute top-0 bottom-0 w-px border-r border-dotted border-gray-200 opacity-60"
                        style={{ left: `${((index + 1) * 2 / 12) * 100}%` }}
                    ></div>
                ))}

                {tasks.map((task, index) => {
                    // Calculate position and width more precisely
                    const startPos = ((task.startWeek - 1) / 12) * 100;
                    const width = ((task.endWeek - task.startWeek + 1) / 12) * 100;

                    // Ensure the card doesn't go beyond container bounds
                    const maxWidth = 100 - startPos;
                    const cardWidth = Math.min(Math.max(width, 20), maxWidth);

                    const taskTheme = getTaskTheme(task.id);

                    return (
                        <div key={task.id} className="relative">
                            <div
                                className={`${taskTheme.cardBg} rounded-xl border ${taskTheme.border} shadow-sm hover:shadow-md transition-all duration-300 p-5 ${taskTheme.hover} group relative overflow-hidden`}
                                style={{
                                    marginLeft: `${Math.min(startPos, 75)}%`, // Cap at 75% to ensure visibility
                                    width: `${cardWidth}%`,
                                    minWidth: '260px',
                                    maxWidth: '100%'
                                }}
                            >
                                {/* Status Bar - Themed */}
                                <div className={`h-1 ${taskTheme.statusBar} rounded-sm mb-4`}></div>

                                {/* Content - More compact */}
                                <div className="space-y-3">
                                    <div>
                                        <h4 className={`font-bold text-lg ${taskTheme.titleColor} leading-tight mb-1`}>
                                            {task.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                            {task.subtitle}
                                        </p>
                                    </div>

                                    {/* Footer - More compact */}
                                    <div className="flex items-center justify-between pt-3">
                                        <span className={`text-xs font-medium px-3 py-1.5 rounded-md ${taskTheme.statusBadge}`}>
                                            {getStatusLabel(task.status)}
                                        </span>

                                        {/* Team Size Indicator - Minimal */}
                                        {task.teamSize && (
                                            <div className="flex items-center space-x-1 text-gray-500">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                                                </svg>
                                                <span className="text-xs font-medium">{task.teamSize}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bottom Summary */}
            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-between items-center">
                <div className="flex space-x-8">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Duration</div>
                        <div className="text-xl font-bold text-gray-900">12 Weeks</div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Team Size</div>
                        <div className="text-xl font-bold text-gray-900">6 Members</div>
                    </div>
                </div>

                {/* Progress Indicator */}
                <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Completed & Delivered</span>
                </div>
            </div>
        </div>
    );
};

export default ProjectTimeline; 